import { createSelector } from '@ngrx/store'
import { LabelMaps, Ranking } from 'globals/store/events.data';
import { WFWorker } from 'globals/store/workforce.data'
import { SponsorsTableData, SponsorSessionTableData } from 'src/app/helpers/table-column-types'
import { makeMapFromListOfObjectsAndKey, getList, getSet, setDifference, sortByProp, getNestedList } from 'globals/helpers/helpers'
import { Sponsor, sponsorStatus } from 'globals/store/sponsors.data';
import { eventStateSelector, LabelObject } from 'src/app/store/events.era';
import { getBooleanLight, getActiveLights, sortByDateAndStart, ProgramsSponsors, SessionsEntitiesData, SessionSponsorData, SponsorSessionInfo, NestedProgramInfo, fullSessionsEntitiesReselector, filteredSponsorsProgramsReselector, sessionsEntitiesReselector, SponsorSessionInfoLite, getSponsorStatusLights, SessionInfo, SearchableSessionFields, fullSponsorsProgramsReselector, getBooleanConfirmedLight } from './performerSponsors.reselector'
import { PublicEditProfileInfo, shareUrlsByType } from 'globals/store/share.data'
import { performerStatusNames } from '../performers.era';
import { tableLightColors } from 'src/app/helpers/table-helpers';
import { environment } from '../../../../src/environments/environment';
import { getBaseSponsorTableData, getReadyAssetLights } from './entity.decircular.reselector';
import { allAssociatedAssetsIdsReselector } from './entity.reselector';
import { sessionMapReselector } from './sessions.reselector';
import { Dictionary } from '@ngrx/entity';
import { Asset } from 'globals/store/assets.data';
import { Session } from 'globals/store/programs.data';
import { workforceStateSelector } from '../workforce.era';
import { sponsorsStateSelector } from '../sponsors.era';
import { shareStateSelector } from '../share.era';
import { filterStateSelector } from '../filters.era';

const workersByEmail = createSelector(workforceStateSelector, (store) => store.workersByEmail)
const sponsorEntities = createSelector(sponsorsStateSelector, (store) => store.entities)
const labelMaps = createSelector(eventStateSelector, (store) => store.labelMaps)
const sponsorProfileEditShareables = createSelector(shareStateSelector, state => state.sponsor)
const sponsorTypes = createSelector(sponsorsStateSelector, (store) => store.sponsorTypes)
const sessionLabels = createSelector(eventStateSelector, (store) => store.labelMaps.session_labels)
const programLabels = createSelector(eventStateSelector, (store) => store.labelMaps.program_labels)
const sponsorTableFilters = createSelector(filterStateSelector, (store) => store.sponsors)
const sponsorSessionTableFilters = createSelector(filterStateSelector, (store) => store.sponsor_sessions)
const assets = (store)=> store.assets.entities

const SearchableSponsorFields = ['name']

export const extractSingleSessionSponsorsData = (sessionInfo: SessionInfo, filteredSponsorsList: Sponsor[], sponsors: { [id: string]: Sponsor }): SessionInfo => {
  const usedSponsorIds = getSet(sessionInfo.sponsorInfos, 'sponsorId')
  const unusedSponsorIds: Set<string> = !sponsors ? new Set() : setDifference<string>(new Set(filteredSponsorsList.map(s => s.id)), usedSponsorIds)
  return {
    ...sessionInfo,
    sponsors: !sponsors ? [] : Array.from(usedSponsorIds).map(id => sponsors[id]).filter(p => p).sort(sortByProp('name')),
    unusedSponsors: !sponsors ? [] : Array.from(unusedSponsorIds).map(id => sponsors[id]).filter(p => p).sort(sortByProp('name'))
  }
}

export const singleSessionSponsorsReselector = createSelector( // for manage session and the performers component
  fullSessionsEntitiesReselector,
  fullSponsorsProgramsReselector,
  sponsorEntities,
  ({ sessionInfos }: SessionsEntitiesData, { sponsorList }: ProgramsSponsors, sponsors: { [id: string]: Sponsor }, sessionId: string) => sessionInfos && sessionId && sessionInfos[sessionId] ? extractSingleSessionSponsorsData(sessionInfos[sessionId], sponsorList, sponsors) : null
)



export const sponsorContactReselector = createSelector(
  workersByEmail,
  sponsorEntities,
  (workersByEmail, sponsors, sponsorId: string) => {
    if (!workersByEmail || !sponsors) return { contact: null, sponsor: null }
    const sponsor: Sponsor = sponsors[sponsorId]
    if (!sponsor) return { contact: null, sponsor: null }
    return { sponsor, contact: workersByEmail[sponsor.WFContact] } // contact can be undefined
  }
)

export const sessionSponsorReselector = createSelector( // for manage performer
  fullSessionsEntitiesReselector,
  ({ sessionInfos, sponsorSessionInfosBySponsorId, unusedSessionIdsBySponsorId }: SessionsEntitiesData, sponsorId: string): SessionSponsorData => {

    const sSessionInfos = sponsorSessionInfosBySponsorId[sponsorId]
    if (!sSessionInfos) return { sessionList: [], sessionProgramList: [] }

    const sessionList: SponsorSessionInfo[] = sSessionInfos.map(({ sessionId, status }) => ({ ...sessionInfos[sessionId], status }))

    sessionList.sort(sortByDateAndStart)
    const unusedSessionIds = unusedSessionIdsBySponsorId[sponsorId]
    unusedSessionIds.sort((a, b) => sortByDateAndStart(sessionInfos[a], sessionInfos[b]))

    const sessionProgramList: NestedProgramInfo[] = []

    let programListIndex = 0
    const programIdToListLocation = {}
    unusedSessionIds.forEach(id => {
      const session = sessionInfos[id]
      const { programId, programTitle } = session
      if (!programIdToListLocation.hasOwnProperty(programId)) {
        programIdToListLocation[programId] = programListIndex++
        sessionProgramList.push({ programId, programTitle, sessionList: [] })
      }
      sessionProgramList[programIdToListLocation[programId]].sessionList.push({ ...session, selected: false })
    })

    return { sessionList, sessionProgramList }
  }
)


export const sponsorSessionTableReselector = createSelector(
  programLabels,
  sessionLabels,
  sessionsEntitiesReselector,
  sponsorSessionTableFilters,
  (programLabels, sessionLabels, { sessionInfos }, sessionTableFilters ) => {
    let sessionInfoList = sessionInfos ? Object.values(sessionInfos) : []

    const { search } = sessionTableFilters
    // console.log({ search, sessionInfoList })
    if (search) {
      const searchTerm = search.toLocaleLowerCase()
      sessionInfoList = sessionInfoList.filter(s => SearchableSessionFields.some(field => s[field] && s[field].toLocaleLowerCase().includes(searchTerm)))
    }
    // further filtering for sessions with no performers and sponsor filters active
    const sponsorFiltersActive = sessionTableFilters.sponsorLabels || sessionTableFilters.tags
    if (sponsorFiltersActive) {
      sessionInfoList = sessionInfoList.filter(s => s.sponsorInfos.length)
    }

    // console.log({ sessionInfoList })

    const sponsorSessionTableData: SponsorSessionTableData[] = sessionInfoList.map(sessionInfo => getSponsorSessionTableData(sessionInfo, sessionLabels, programLabels))
    const sponsorSessionTableRowsById = makeMapFromListOfObjectsAndKey(sponsorSessionTableData, 'id')
    return { sponsorSessionTableData, sponsorSessionTableRowsById }
  }
)


const getSponsorSessionTableData = (
  sessionInfo: SessionInfo, 
  sessionLabels: LabelObject, 
  programLabels: LabelObject,
): SponsorSessionTableData => {

  const { id, title: name, date, start, duration, end, sponsorInfos, sessionLabels: sLabels, programLabels: pLabels, description, programTitle: programName, day } = sessionInfo
  // console.log({ sessionInfo })
  const HHMMText = `${start} - ${end}` + (!date ? '' : ', ')
  const nestedIds = getList(sponsorInfos, 'sponsorId')
  // console.log({ sponsorInfos })
  const sponsorSummary = getSponsorStatusLights(sponsorInfos, 'sessions')

  const sponsorStatusMap = Object.assign({}, ...sponsorInfos.map(({ sponsorId, status }) => ({
    [sponsorId]: {
      percent: status === sponsorStatus.Confirmed? 1 : status === sponsorStatus.Pending ? 0.5 : status === sponsorStatus.Cancelled ? 0 : -1,
      lights: [{
        text: performerStatusNames[status],
        color: status === sponsorStatus.Confirmed ? tableLightColors.green : status === sponsorStatus.Pending ? tableLightColors.yellow : status === sponsorStatus.Cancelled ? tableLightColors.red : tableLightColors.gray
      }]
    }
  })))

  const sponsorSessionRow: SponsorSessionTableData = {
    id,
    nestedIds,
    sessionInfo,
    sponsorStatus: sponsorStatusMap,
    noNestedText: "This session does not have any sponsors assigned",
    title: { text: name },
    sponsorSummary,
    programName: { text: programName },
    description: { text: description },
    sessionLabels: !sLabels ? [] : sLabels.filter(l => sessionLabels[l]).map(label => ({ color: sessionLabels[label].color, label: sessionLabels[label].label })),
    programLabels: !pLabels ? [] : pLabels.filter(l => programLabels[l]).map(label => ({ color: programLabels[label].color, label: programLabels[label].label })),
    sponsorCount: { text: sponsorInfos.length.toString() },
    time: { HHMMText, date, day, extraDateData: [start, duration + "", end]  },
  }
  return sponsorSessionRow
}
 

export const sponsorsTablePreselector = createSelector( // a selector can only have 10 inputs at most
  workersByEmail,
  labelMaps,
  assets,
  allAssociatedAssetsIdsReselector,
  sessionMapReselector,
  (workersByEmail, labelMaps, assets, associatedAssetIdsByEntityId, sessions ) => 
    ({ workersByEmail, labelMaps, assets, associatedAssetIdsByEntityId, sessions })
)

export const sponsorsTableReselector = createSelector(
  sponsorsTablePreselector,
  filteredSponsorsProgramsReselector,
  sessionsEntitiesReselector,
  sponsorProfileEditShareables,
  sponsorTypes,
  sponsorTableFilters,
  ({ workersByEmail, labelMaps, assets, associatedAssetIdsByEntityId, sessions },
    { sponsorList, programsList }: ProgramsSponsors,
    { sponsorSessionInfosBySponsorId }: SessionsEntitiesData,
    sponsorShareables: { [sId:string]: PublicEditProfileInfo },
    sponsorTypes,
    sponsorTableFilters,
  ) => {
    const { search, types } = sponsorTableFilters

    if (search) {
      const searchTerm = search.toLocaleLowerCase()
      sponsorList = sponsorList.filter(p => SearchableSponsorFields.some(field => p[field] && p[field].toLocaleLowerCase().includes(searchTerm)))
    }
    if (types) sponsorList = sponsorList.filter(s => types.includes(s.sponsorTypeId) || (!s.sponsorTypeId && types.includes('noType')))
    
    const sessionFiltersActive = sponsorTableFilters.sessionLabels || sponsorTableFilters.programLabels || sponsorTableFilters.programs
    if (sessionFiltersActive) {
      sponsorList = sponsorList.filter(p => sponsorSessionInfosBySponsorId[p.id].length)
    }


    const sponsorTableData: SponsorsTableData[] = sponsorList.map((sponsor: Sponsor) => getSponsorTableData(sponsor, workersByEmail, labelMaps.sponsor_labels,
      sponsorSessionInfosBySponsorId, sponsorShareables[sponsor.id], sponsorTypes, associatedAssetIdsByEntityId, assets, sessions ))
    const sponsorTableRowsById = makeMapFromListOfObjectsAndKey(sponsorTableData, 'id')
    return { sponsorTableData, sponsorTableRowsById }
  }
)

const getSponsorTableData = ( sponsor: Sponsor, workersByEmail: { [key: string]: WFWorker }, sponsorLabels: LabelObject, 
  sponsorSessionInfosBySponsorId: { [sId:string]: SponsorSessionInfoLite[] }, shareable: PublicEditProfileInfo, sponsorTypes: { [id: string]: Ranking }, 
  associatedAssetIdByEntityId: Dictionary<string[]>, assets: Dictionary<Asset>, sessions: Dictionary<Session>): SponsorsTableData => {

  const { id, name, WFContact, confirmed, labels, sponsorTypeId, hideInPublish } = sponsor
  const contact = workersByEmail[WFContact]

  const sponsorType = !sponsorTypeId || !sponsorTypes[sponsorTypeId] ? null : sponsorTypes[sponsorTypeId]
  const sessionsInfos = sponsorSessionInfosBySponsorId[id] || []
  const sessionIds = getList(sessionsInfos, 'sessionId')
  const sessionSummary = getSponsorStatusLights(sessionsInfos, 'sessions')

  const sessionAssetIds = Array.from(new Set(getNestedList(sessionIds.map(id => sessions[id]), 'assetIds').filter(id => !!assets[id])))

  const sponsorStatusMap = Object.assign({}, ...sessionsInfos.map(({ sessionId, status }) => ({
    [sessionId]: {
      percent: status === sponsorStatus.Confirmed? 1 : status === sponsorStatus.Pending ? 0.5 : status === sponsorStatus.Cancelled ? 0 : -1,
      lights: [{
        text: performerStatusNames[status],
        color: status === sponsorStatus.Confirmed ? tableLightColors.green : status === sponsorStatus.Pending ? tableLightColors.yellow : status === sponsorStatus.Cancelled ? tableLightColors.red : tableLightColors.gray
      }]
    }
  })))

  const sponsorRow: SponsorsTableData = {
    ...getBaseSponsorTableData(sponsor),
    sponsorStatus: sponsorStatusMap,
    detailsConfirmed: getBooleanConfirmedLight(confirmed),
    workforceContact: !contact ? null : contact,
    editingUrlStatus: getActiveLights(!shareable ? null : shareable.status, !shareable ? null : environment.joi.shareBase + shareUrlsByType.sponsor + '/' + shareable.code ),
    labels: labels ? labels.filter(label => sponsorLabels[label]).map(label => ({ color: sponsorLabels[label].color, label: sponsorLabels[label].label })) : [],
    sessionCount: { text: sessionsInfos.length.toString() },
    sessionSummary,
    sponsorType: { rank: !sponsorType ? 0 : sponsorType.rank, text: !sponsorType ? '' : sponsorType.name },
    nestedIds: sessionIds,
    noNestedText: `${name} is not assigned to any sessions`,
    hideInPublish: getBooleanLight(!hideInPublish),
    assets: getReadyAssetLights(associatedAssetIdByEntityId[id], assets),
    sessionAssets: getReadyAssetLights(sessionAssetIds, assets),
  }
  return sponsorRow
}